"use client";

import { getS3ImageUrl } from "@/utils/routes";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { getSectionStyle, toDayHrMinSec } from "../../utils";
import { LeftIcon, RightIcon } from "../Icons";
import ArrowButton from "../buttons/ArrowButton";
import ShowMoreButton from "../buttons/ShowMoreButton";
import classes from "./SectionHeader.module.css";

import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

interface Props extends SectionHeaderType {
  index?: number;
  title: string;
  btnType?: "more" | "all" | "arrow";
  btnText?: string;
  link?: string;
  target?: "_blank";
  timeout?: boolean;
  startTime?: number;
  endDate?: string;
  style?: React.CSSProperties;
  noContainer?: boolean;

  settings?: WidgetSettings;

  filterItems?: WidgetTabType[];
  filterIndex?: number;
  setFilterIndex?: (index: number) => void;

  timeEnded?: () => void;
  onLeftClick?: () => void;
  onRightClick?: () => void;
}

export const Timeout = ({
  startTime,
  endDate,
  timeEnded = () => {},
  type,
  className,
}: {
  startTime?: number;
  endDate: string;
  timeEnded?: () => void;
  type?: "sm" | "lg";
  className?: string;
}) => {
  const [timeDistance, setTimeDistance] = useState(0);

  useEffect(() => {
    const dis = new Date(endDate).getTime() - new Date().getTime();
    setTimeDistance(new Date(endDate).getTime() - new Date().getTime());

    if (dis <= 0) timeEnded();

    const timer = setInterval(() => {
      setTimeDistance((prevTime) => {
        const newTime = prevTime - 1000;

        if (newTime <= 0) timeEnded();

        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate, timeEnded]);

  const timerArr = toDayHrMinSec(timeDistance);

  return (
    <div className={[classes.TimeoutItems, className].join(" ")}>
      {timerArr.map((time, index) => (
        <div
          key={index+"-timeout-item"}
          className={`${classes.Timeout} ${
            timerArr.length > 3 && time.endsWith("s") ? classes.last : ""
          }`}
        >
          <span className={classes.text}>{time}</span>
        </div>
      ))}
    </div>
  );
};

const SectionHeader = ({
  index,
  title,
  btnType = "all",
  btnText,
  timeout = false,
  endDate = "",
  target,
  link,
  align = "left",
  filterItems,
  style,
  noContainer = false,

  settings,

  onLeftClick,
  onRightClick,
  timeEnded = () => {},
  ...props
}: Props) => {
  return (
    <div className={classes.sectionHeader} style={getSectionStyle(settings)}>
      <div className={noContainer ? "" : "container"}>
        <div
          className={classes.contents}
          style={{
            textAlign: align,
            ...(align === "center" && { justifyContent: "center" }),
            ...(align === "right" && {
              justifyContent: "flex-end",
            }),
          }}
        >
          <div
            className={`${classes.left}`}
            style={{
              ...(timeout && { flex: 1 }),
            }}
          >
            <h2 className={`${classes.title} flex-grow`}>{title}</h2>

            {timeout && new Date().getTime() < new Date(endDate).getTime() && (
              <div className={classes.middle}>
                <Timeout
                  endDate={endDate}
                  timeEnded={timeEnded}
                  className={classes.timeout}
                />
              </div>
            )}
          </div>

          {btnType && link && link !== "" && link !== "#" && (
            <Link href={link}>
              <div className={classes.right}>
                {btnType === "arrow" ? (
                  <div className={classes.arrow}>
                    <ArrowButton
                      type="left"
                      onClick={onLeftClick}
                      className={classes.left}
                    />
                    <ArrowButton type="right" onClick={onRightClick} />
                  </div>
                ) : (
                  <ShowMoreButton type={btnType} text={btnText} />
                )}
              </div>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export const SectionHeaderFilter = ({
  index,
  title,
  btnType = "all",
  btnText,
  timeout = false,
  endDate = "",
  target,
  link,
  align = "left",
  onLeftClick,
  onRightClick,
  timeEnded = () => {},

  settings,

  filterItems,
  filterIndex,
  setFilterIndex = () => {},

  ...props
}: Props) => {
  const randomKey = useMemo(
    () => index?.toString() || "filter",
    [index]
  );

  return (
    <div
      className={`${classes.sectionHeader} ${classes.filter}`}
      style={getSectionStyle(settings)}
    >
      <div className="container">
        <div className={classes.contents}>
          <div
            className={classes.left}
            style={{
              ...(timeout && { flex: 1 }),
            }}
          >
            <h2 className={classes.title}>{title}</h2>

            <div className={classes.filterContainer}>
              {!!filterItems?.length && (
                <div className={classes.filterItems}>
                  <div className={classes.changer}>
                    <button
                      className={`${classes.button} ${classes.left}`}
                      id={"filter-products-w-prev-" + randomKey}
                    >
                      <LeftIcon className={classes.icon} />
                    </button>

                    <button
                      className={`${classes.button} ${classes.right}`}
                      id={"filter-products-w-next-" + randomKey}
                    >
                      <RightIcon className={classes.icon} />
                    </button>
                  </div>

                  <Swiper
                    spaceBetween={24}
                    navigation={{
                      nextEl: "#filter-products-w-next-" + randomKey,
                      prevEl: "#filter-products-w-prev-" + randomKey,
                    }}
                    slidesPerView={"auto"}
                    slidesPerGroup={3}
                    pagination={{
                      clickable: true,
                    }}
                    className={classes.swiper}
                    modules={[Navigation]}
                  >
                    {filterItems?.map((item, index) => {
                      let classNames = classes.filterItem;
                      if (index === filterIndex)
                        classNames += ` ${classes.active}`;

                      return (
                        <SwiperSlide
                          key={index + "-filter-item"}
                          style={{
                            width: "auto",
                          }}
                        >
                          <button
                            className={classNames}
                            onClick={() => setFilterIndex(index)}
                          >
                            {item.logo && (
                              <Image
                                src={getS3ImageUrl(item.logo || "")}
                                alt={item.name??'filter item image'}
                                unoptimized={true}
                                layout="responsive"
                                width={50}
                                height={20}
                                className="max-w-[50px] max-h-[30px] md:max-w-[90px] md:max-h-[45px]"
                                style={{
                                  objectFit: "contain",
                                  objectPosition: "center",
                                  // maxWidth: 90,
                                  // maxHeight: 45,
                                  minWidth: 16,
                                  minHeight: 16,
                                }}
                              />
                            )}

                            {item.name && (
                              <p className={classes.name}>{item.name}</p>
                            )}
                          </button>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                </div>
              )}
              {/* <div className={`${classes.filterItems} hide-scrollbar`}>
                                {filterItems?.map((item, index) => {
                                    let classNames = classes.filterItem;
                                    if (index === filterIndex) classNames += ` ${classes.active}`;

                                    return (
                                        <button key={index}
                                            className={classNames}
                                            onClick={() => setFilterIndex(index)}
                                        >
                                            {item.logo && (
                                                <Image 
                                                    src={getS3ImageUrl(item.logo || '')}
                                                    alt={item.name}
                                                    unoptimized={true}
                                                    layout="responsive"
                                                    width={50}
                                                    height={20}
                                                    style={{
                                                        objectFit: 'contain',
                                                        objectPosition: 'center',
                                                        maxWidth: 50,
                                                        maxHeight: 20,
                                                        minWidth: 16,
                                                        minHeight: 16,
                                                    }}
                                                />
                                            )}

                                            <p className={classes.name}>
                                                {item.name}
                                            </p>
                                        </button>
                                    )
                                })}
                            </div> */}
            </div>
          </div>

          {btnType && link && link !== "" && link !== "#" && (
            <div className={classes.right}>
              <Link href={link}>
                {btnType === "arrow" ? (
                  <div className={classes.arrow}>
                    <ArrowButton
                      type="left"
                      onClick={onLeftClick}
                      className={classes.left}
                    />
                    <ArrowButton type="right" onClick={onRightClick} />
                  </div>
                ) : (
                  <ShowMoreButton type={btnType} text={btnText} />
                )}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionHeader;
